import axios from "axios"
import React, { useEffect, useMemo, useRef, useState } from "react"
import { Button, Card, Form, Spinner } from "react-bootstrap"
import { Navigate } from "react-router-dom"
import CustomSelect from "../../components/CustomSelect"
import PageTitleButton from "../../components/PageTitleButton"
import { admin } from "../../config/apiEndpoints"
import axiosProvider from "../../lib/AxiosProvider"
import {
  GetUserToken,
  HasModulePermission,
  useHandleUnauthorizedAccess,
} from "../../lib/GlobalMethods"
import { modulePermission } from "../../lib/GlobalVariables"
import { toast } from "react-toastify"

const serviceOptions = [
  { label: "All Services", value: "all" },
  { label: "MNIL API", value: "mnil-api" },
  { label: "MNIL OCPP Server", value: "mnil-ocpp-server" },
]

const logTypeOptions = [
  { label: "Output Logs", value: "out" },
  { label: "Error Logs", value: "error" },
]

const lineOptions = [
  { label: "50 lines", value: 50 },
  { label: "100 lines", value: 100 },
  { label: "250 lines", value: 250 },
  { label: "500 lines", value: 500 },
]

const ProductionLogs = () => {
  const canView = HasModulePermission(modulePermission.productionLogs, "view")
  const { handleAccess } = useHandleUnauthorizedAccess()
  const token = GetUserToken()
  const [service, setService] = useState(serviceOptions[0])
  const [logType, setLogType] = useState(logTypeOptions[0])
  const [station, setStation] = useState(null)
  const [stationOptions, setStationOptions] = useState([])
  const [charger, setCharger] = useState(null)
  const [bookingId, setBookingId] = useState("")
  const [chargerOptions, setChargerOptions] = useState([])
  const [fromDate, setFromDate] = useState("")
  const [toDate, setToDate] = useState("")
  const [lines, setLines] = useState(lineOptions[1])
  const [logs, setLogs] = useState([])
  const [nextCursor, setNextCursor] = useState(null)
  const [loading, setLoading] = useState(false)
  const [exporting, setExporting] = useState(null)
  const [liveEnabled, setLiveEnabled] = useState(true)
  const [historyReady, setHistoryReady] = useState(false)
  const [liveStatus, setLiveStatus] = useState("disconnected")
  const lastLiveTimestampRef = useRef(null)
  const seenLiveIdsRef = useRef(new Set())

  const today = new Date().toISOString().slice(0, 10)

  const availableChargerOptions = useMemo(() => {
    if (!station?.chargerIds) return chargerOptions
    const stationChargers = new Set(station.chargerIds)
    return chargerOptions.filter((option) => stationChargers.has(option.value))
  }, [chargerOptions, station])

  const validateDateRange = () => {
    if (Boolean(fromDate) !== Boolean(toDate)) {
      toast.error("Please select both From Date and To Date")
      return false
    }

    if (!fromDate) return true

    const from = new Date(`${fromDate}T00:00:00Z`)
    const to = new Date(`${toDate}T23:59:59.999Z`)

    if (from > to) {
      toast.error("From Date must be before or equal to To Date")
      return false
    }

    return true
  }

  const buildSearchParams = ({ format, cursor } = {}) => {
    const searchParams = new URLSearchParams({
      service: service.value,
      type: logType.value,
    })

    if (!format) {
      searchParams.set("lines", String(lines.value))
    }
    if (station?.value) {
      searchParams.set("station_id", station.value)
    }
    if (charger?.value) {
      searchParams.set("charger", charger.value)
    }
    if (bookingId.trim()) {
      searchParams.set("booking_id", bookingId.trim())
    }
    if (fromDate && toDate) {
      searchParams.set("from_date", fromDate)
      searchParams.set("to_date", toDate)
    }
    if (format) {
      searchParams.set("export", format)
    }
    if (cursor) {
      searchParams.set("cursor", cursor)
    }

    return searchParams
  }

  const getErrorMessage = async (error, fallback) => {
    const errorData = error?.response?.data || error?.data

    if (errorData instanceof Blob) {
      try {
        const parsed = JSON.parse(await errorData.text())
        return parsed?.message || fallback
      } catch {
        return fallback
      }
    }

    return errorData?.message || error?.message || fallback
  }

  const fetchLogs = async ({ cursor: requestedCursor, prepend = false } = {}) => {
    if (!validateDateRange()) return
    setHistoryReady(false)
    setLoading(true)

    try {
      const searchParams = buildSearchParams({ cursor: requestedCursor })
      const response = await axiosProvider({
        method: "GET",
        endpoint: `${admin.productionLogs}?${searchParams.toString()}`,
      })
      const responseData = response?.data || response?.response?.data

      handleAccess({ code: responseData?.code })

      if (!responseData?.success) {
        throw new Error(responseData?.message || "Failed to fetch logs")
      }

      const receivedLogs = Array.isArray(responseData.logs) ? responseData.logs : []
      setLogs((current) => prepend ? [...receivedLogs, ...current] : receivedLogs)
      setNextCursor(responseData?.pagination?.next_cursor || null)
      const newestTimestamp = responseData?.entries
        ?.map((entry) => entry?.timestamp)
        ?.filter(Boolean)
        ?.sort()
        ?.at(-1)
      if (!prepend && newestTimestamp) lastLiveTimestampRef.current = newestTimestamp
      for (const entryId of responseData?.entry_ids || []) {
        seenLiveIdsRef.current.add(entryId)
      }
    } catch (error) {
      setLogs([])
      toast.error(await getErrorMessage(error, "Failed to fetch production logs"))
    } finally {
      setLoading(false)
      setHistoryReady(true)
    }
  }

  const fetchFilterOptions = async () => {
    try {
      const [stationResponse, chargerResponse] = await Promise.all([
        axiosProvider({
          method: "GET",
          endpoint: `${admin.chargingStationList}?active=true`,
        }),
        axiosProvider({
          method: "GET",
          endpoint: `${admin.chargerList}?page=1&limit=500`,
        }),
      ])

      const stationData =
        stationResponse?.data || stationResponse?.response?.data
      const chargerData =
        chargerResponse?.data || chargerResponse?.response?.data

      handleAccess({ code: stationData?.code || chargerData?.code })

      if (stationData?.code === 200) {
        setStationOptions(
          (stationData.data || []).map((item) => ({
            value: item.id,
            label: `${item.id} - ${item.station_name}`,
            chargerIds: (item.chargers || [])
              .map((mappedCharger) => mappedCharger.id)
              .filter(Boolean),
          })),
        )
      }

      if (chargerData?.code === 200) {
        setChargerOptions(
          (chargerData.data || [])
            .map(({ id }) => ({ value: id, label: id }))
            .filter((option) => option.value),
        )
      }
    } catch (error) {
      toast.error(await getErrorMessage(error, "Failed to load log filters"))
    }
  }

  const exportLogs = async (format) => {
    if (!validateDateRange()) return
    setExporting(format)

    try {
      const searchParams = buildSearchParams({ format })
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}${admin.productionLogs}?${searchParams.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "User-Type": "Admin",
          },
          responseType: "blob",
        },
      )

      const contentType = response?.headers["content-type"] || ""
      const expectedType = format === "csv" ? "text/csv" : "text/plain"
      if (!contentType.includes(expectedType)) {
        throw new Error("The server returned an unexpected export format")
      }

      const disposition = response?.headers["content-disposition"] || ""
      const filenameMatch = disposition.match(/filename="?([^"]+)"?/)
      const filename =
        filenameMatch?.[1] ||
        `production-logs-${new Date().toISOString().slice(0, 10)}.${format}`
      const url = window.URL.createObjectURL(response.data)
      const link = document.createElement("a")
      link.href = url
      link.setAttribute("download", filename)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
      toast.success(`${format.toUpperCase()} export downloaded successfully`)
    } catch (error) {
      toast.error(await getErrorMessage(error, "Failed to export production logs"))
    } finally {
      setExporting(null)
    }
  }

  const handleStationChange = (option) => {
    setStation(option)
    setCharger(null)
  }

  useEffect(() => {
    if (canView) {
      fetchFilterOptions()
      fetchLogs()
    }
  }, [canView])

  useEffect(() => {
    if (!canView || !historyReady || !liveEnabled || fromDate || toDate) {
      setLiveStatus("disconnected")
      return undefined
    }

    const controller = new AbortController()
    let reconnectTimer
    let retryMs = 1000

    const connect = async () => {
      setLiveStatus("connecting")
      try {
        const params = buildSearchParams()
        params.delete("lines")
        if (lastLiveTimestampRef.current) {
          params.set("since", lastLiveTimestampRef.current)
        }
        const response = await fetch(
          process.env.REACT_APP_BASE_URL + admin.productionLogs + "/stream?" + params.toString(),
          {
            headers: {
              Authorization: "Bearer " + token,
              "User-Type": "Admin",
              Accept: "text/event-stream",
            },
            signal: controller.signal,
          },
        )
        if (!response.ok || !response.body) {
          throw new Error("Live log stream failed with status " + response.status)
        }

        setLiveStatus("connected")
        retryMs = 1000
        const reader = response.body.getReader()
        const decoder = new TextDecoder()
        let buffer = ""

        while (!controller.signal.aborted) {
          const { value, done } = await reader.read()
          if (done) break
          buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, "\n")
          let boundary
          while ((boundary = buffer.indexOf("\n\n")) >= 0) {
            const frame = buffer.slice(0, boundary)
            buffer = buffer.slice(boundary + 2)
            let eventName = "message"
            let eventId = ""
            const dataLines = []
            for (const line of frame.split("\n")) {
              if (line.startsWith("event:")) eventName = line.slice(6).trim()
              else if (line.startsWith("id:")) eventId = line.slice(3).trim()
              else if (line.startsWith("data:")) dataLines.push(line.slice(5).trimStart())
            }
            if (eventName !== "log" || !dataLines.length) continue
            if (eventId && seenLiveIdsRef.current.has(eventId)) continue
            const entry = JSON.parse(dataLines.join("\n"))
            if (eventId) {
              seenLiveIdsRef.current.add(eventId)
              if (seenLiveIdsRef.current.size > 5000) {
                const oldest = seenLiveIdsRef.current.values().next().value
                seenLiveIdsRef.current.delete(oldest)
              }
            }
            if (entry?.timestamp &&
                (!lastLiveTimestampRef.current ||
                 entry.timestamp > lastLiveTimestampRef.current)) {
              lastLiveTimestampRef.current = entry.timestamp
            }
            setLogs((current) =>
              [...current, JSON.stringify(entry)].slice(-lines.value),
            )
          }
        }
        if (!controller.signal.aborted) throw new Error("Live stream disconnected")
      } catch (error) {
        if (controller.signal.aborted) return
        setLiveStatus("reconnecting")
        reconnectTimer = window.setTimeout(connect, retryMs)
        retryMs = Math.min(retryMs * 2, 15000)
      }
    }

    connect()
    return () => {
      controller.abort()
      window.clearTimeout(reconnectTimer)
    }
  }, [
    canView,
    liveEnabled,
    historyReady,
    service,
    station,
    charger,
    bookingId,
    fromDate,
    toDate,
    logType,
    lines,
    token,
  ])

  if (!canView) {
    return <Navigate to="/dashboard" replace />
  }

  return (
    <>
      <PageTitleButton PagetitleText="Production Logs" />

      <Card className="production_logs_filters mb-3">
        <Card.Body>
          <div className="row g-3 align-items-end">
            <Form.Group className="col-12 col-md-6 col-xl-3">
              <Form.Label>Service</Form.Label>
              <CustomSelect
                value={service}
                options={serviceOptions}
                handleSelectChange={(option) => option && setService(option)}
                isSearchable={false}
              />
            </Form.Group>

            <Form.Group className="col-12 col-md-6 col-xl-3">
              <Form.Label>Log Type</Form.Label>
              <CustomSelect
                value={logType}
                options={logTypeOptions}
                handleSelectChange={(option) => option && setLogType(option)}
                isSearchable={false}
              />
            </Form.Group>

            <Form.Group className="col-12 col-md-6 col-xl-3">
              <Form.Label>Charging Station</Form.Label>
              <CustomSelect
                value={station}
                options={stationOptions}
                handleSelectChange={handleStationChange}
                placeholder="All stations"
                isSearchable
              />
            </Form.Group>

            <Form.Group className="col-12 col-md-6 col-xl-3">
              <Form.Label>Charger ID</Form.Label>
              <CustomSelect
                value={charger}
                options={availableChargerOptions}
                handleSelectChange={setCharger}
                placeholder={station ? "All station chargers" : "Optional"}
                isSearchable
              />
            </Form.Group>

            <Form.Group className="col-12 col-md-6 col-xl-3">
              <Form.Label>From Date</Form.Label>
              <Form.Control
                type="date"
                value={fromDate}
                max={toDate || today}
                onChange={(event) => setFromDate(event.target.value)}
              />
            </Form.Group>

            <Form.Group className="col-12 col-md-6 col-xl-3">
              <Form.Label>Booking ID</Form.Label>
              <Form.Control
                type="text"
                value={bookingId}
                placeholder="Optional"
                onChange={(event) => setBookingId(event.target.value)}
              />
            </Form.Group>

            <Form.Group className="col-12 col-md-6 col-xl-3">
              <Form.Label>To Date</Form.Label>
              <Form.Control
                type="date"
                value={toDate}
                min={fromDate || undefined}
                max={today}
                onChange={(event) => setToDate(event.target.value)}
              />
            </Form.Group>

            <Form.Group className="col-12 col-md-6 col-xl-2">
              <Form.Label>Number of Lines</Form.Label>
              <CustomSelect
                value={lines}
                options={lineOptions}
                handleSelectChange={(option) => option && setLines(option)}
                isSearchable={false}
              />
            </Form.Group>

            <div className="col-12 col-md-6 col-xl-2">
              <Form.Label>Live updates</Form.Label>
              <Form.Check
                type="switch"
                id="production-logs-live"
                label={
                  fromDate || toDate
                    ? "Disabled for history"
                    : liveStatus === "connected"
                      ? "Connected"
                      : liveStatus === "reconnecting"
                        ? "Reconnecting"
                        : "Enable live"
                }
                checked={liveEnabled && !fromDate && !toDate}
                disabled={Boolean(fromDate || toDate)}
                onChange={(event) => setLiveEnabled(event.target.checked)}
              />
            </div>

            <div className="col-12 col-md-6 col-xl-2">
              <Button
                type="button"
                className="w-100 d-inline-flex gap-2 align-items-center justify-content-center"
                onClick={() => fetchLogs()}
                disabled={loading || Boolean(exporting)}
              >
                {loading ? <Spinner size="sm" animation="border" /> : null}
                {loading ? "Loading" : "Fetch Logs"}
              </Button>
            </div>

            <div className="col-12 col-md-6 col-xl-1">
              <Button
                type="button"
                variant="outline-primary"
                className="w-100"
                onClick={() => exportLogs("csv")}
                disabled={loading || Boolean(exporting)}
              >
                {exporting === "csv" ? (
                  <Spinner size="sm" animation="border" />
                ) : (
                  "CSV"
                )}
              </Button>
            </div>

            <div className="col-12 col-md-6 col-xl-1">
              <Button
                type="button"
                variant="outline-secondary"
                className="w-100"
                onClick={() => exportLogs("txt")}
                disabled={loading || Boolean(exporting)}
              >
                {exporting === "txt" ? (
                  <Spinner size="sm" animation="border" />
                ) : (
                  "TXT"
                )}
              </Button>
            </div>
          </div>
        </Card.Body>
      </Card>

      <Card className="production_logs_viewer">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <span>Log Output</span>
          <div className="d-flex align-items-center gap-2">
            {nextCursor ? (
              <Button
                type="button"
                size="sm"
                variant="outline-secondary"
                onClick={() => fetchLogs({ cursor: nextCursor, prepend: true })}
                disabled={loading || Boolean(exporting)}
              >
                Load older
              </Button>
            ) : null}
            <span className="text-muted fs_14">{logs.length} lines</span>
          </div>
        </Card.Header>
        <Card.Body className="p-0">
          {loading ? (
            <div className="production_logs_state">
              <Spinner animation="border" variant="primary" />
              <span>Loading production logs...</span>
            </div>
          ) : logs.length ? (
            <div className="production_logs_content" role="log" aria-live="polite">
              {logs.map((log, index) => (
                <div className="production_log_line" key={`${index}-${log}`}>
                  <span className="production_log_number">{index + 1}</span>
                  <span className="production_log_message">{log}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="production_logs_state">No logs available.</div>
          )}
        </Card.Body>
      </Card>
    </>
  )
}

export default ProductionLogs
